import React, { useEffect, useMemo, useState } from "react";
import {
    RefreshCw,
    Search,
    Wifi,
    Battery,
    Thermometer,
    Link2,
    AlertTriangle,
    CheckCircle2,
} from "lucide-react";

async function getJSON(url) {
    const res = await fetch(url, {
        credentials: "include",
        headers: {
            Accept: "application/json",
        },
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        throw new Error(`Risposta non JSON da ${url}: ${text.slice(0, 200)}`);
    }

    if (!res.ok || !data?.ok) {
        throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
    }

    return data;
}

async function postJSON(url, payload) {
    const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        body: JSON.stringify(payload),
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        throw new Error(`Risposta non JSON da ${url}: ${text.slice(0, 200)}`);
    }

    if (!res.ok || !data?.ok) {
        throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
    }

    return data;
}

function fmtDateTime(v) {
    if (!v) return "â€”";
    const d = new Date(String(v).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString("it-IT");
}

function fmtTemp(v) {
    if (v === null || v === undefined || v === "") return "â€”";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return `${n.toFixed(1)}Â°C`;
}

function fmtBattery(v) {
    if (v === null || v === undefined || v === "") return "â€”";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return `${n.toFixed(0)}%`;
}

function statusColor(item) {
    if (item.alert_ultimo) return "text-red-600";
    if (item.last_seen) return "text-green-600";
    return "text-slate-500";
}

function frigoLabel(frigo) {
    return `#${frigo.id} - ${frigo.nome || `Frigo ${frigo.id}`} (${frigo.mac_address || "senza MAC"})`;
}

function frigoMatchesQuery(frigo, query) {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [
        frigo.id,
        frigo.frigo_id,
        frigo.nome,
        frigo.mac_address,
        frigo.azienda_id,
        frigo.tipo_conservazione,
    ]
        .filter((v) => v !== null && v !== undefined && v !== "")
        .some((v) => String(v).toLowerCase().includes(q));
}

export default function LorawanDevicesPage() {
    const [loading, setLoading] = useState(true);
    const [savingMac, setSavingMac] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [devices, setDevices] = useState([]);
    const [frighi, setFrighi] = useState([]);

    const [search, setSearch] = useState("");
    const [onlyUnassigned, setOnlyUnassigned] = useState(false);

    const [selectedFrigoByMac, setSelectedFrigoByMac] = useState({});
    const [frigoSearchByMac, setFrigoSearchByMac] = useState({});
    const [manualFrigoByMac, setManualFrigoByMac] = useState({});

    async function loadAll() {
        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const [devicesRes, frighiRes] = await Promise.all([
                getJSON(
                    `/api/admin/lorawan_devices_list.php?limit=300&only_unassigned=${onlyUnassigned ? 1 : 0
                    }&q=${encodeURIComponent(search)}`
                ),
                fetch(`/api/api_cryo_list.php?legacy=1&limit=500`, {
                    credentials: "include",
                    headers: { Accept: "application/json" },
                }).then(async (res) => {
                    const text = await res.text();
                    let data = [];
                    try {
                        data = text ? JSON.parse(text) : [];
                    } catch {
                        throw new Error("Risposta non valida da api_cryo_list.php");
                    }
                    if (!res.ok) {
                        throw new Error(data?.error || `HTTP ${res.status}`);
                    }
                    return Array.isArray(data) ? data : [];
                }),
            ]);

            setDevices(devicesRes.items || []);
            setFrighi(frighiRes || []);

            // preseleziona il frigo giÃ  assegnato se presente
            const initialMap = {};
            for (const d of devicesRes.items || []) {
                if (d.frigo_id) {
                    initialMap[d.mac_address] = String(d.frigo_id);
                }
            }
            setSelectedFrigoByMac((prev) => ({ ...initialMap, ...prev }));
            setManualFrigoByMac((prev) => ({ ...initialMap, ...prev }));
        } catch (err) {
            setError(err.message || "Errore di caricamento");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredDevices = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return devices;
        return devices.filter((d) => {
            return [
                d.mac_address,
                d.external_device_id,
                d.device_model,
                d.nome,
                d.frigo_nome,
                d.frigo_id,
                d.azienda_id,
            ]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q));
        });
    }, [devices, search]);

    function getFrighiForDevice(device) {
        const currentQuery = frigoSearchByMac[device.mac_address] || "";
        const deviceAziendaId = Number(device.azienda_id || device.frigo_azienda_id || 0);
        const currentFrigoId = Number(device.frigo_id || 0);

        return frighi
            .filter((f) => {
                const sameCompany =
                    !deviceAziendaId ||
                    !Number(f.azienda_id || 0) ||
                    Number(f.azienda_id) === deviceAziendaId ||
                    Number(f.id) === currentFrigoId;
                return sameCompany && frigoMatchesQuery(f, currentQuery);
            })
            .sort((a, b) => {
                if (Number(a.id) === currentFrigoId) return -1;
                if (Number(b.id) === currentFrigoId) return 1;
                return String(a.nome || "").localeCompare(String(b.nome || ""), "it");
            })
            .slice(0, 40);
    }

    async function handleAssign(device, force = false) {
        const manualFrigoId = String(manualFrigoByMac[device.mac_address] || "").trim();
        const frigoId = manualFrigoId || selectedFrigoByMac[device.mac_address];
        if (!frigoId) {
            setError(`Seleziona un frigo o inserisci un frigo_id per ${device.mac_address}`);
            return;
        }

        if (!/^\d+$/.test(String(frigoId))) {
            setError(`Il frigo_id deve essere numerico per ${device.mac_address}`);
            return;
        }

        setSavingMac(device.mac_address);
        setError("");
        setSuccess("");

        try {
            await postJSON("/api/admin/lorawan_assign.php", {
                frigo_id: Number(frigoId),
                mac_address: device.mac_address,
                force: force ? 1 : 0,
            });

            setSuccess(`Dispositivo ${device.mac_address} assegnato correttamente.`);
            await loadAll();
        } catch (err) {
            if (String(err.message).includes("DEVICE_ALREADY_ASSIGNED")) {
                const confirmForce = window.confirm(
                    "Questo dispositivo risulta giÃ  assegnato a un altro frigo. Vuoi forzare lo spostamento?"
                );
                if (confirmForce) {
                    await handleAssign(device, true);
                    return;
                }
            }
            setError(err.message || "Errore durante l'assegnazione");
        } finally {
            setSavingMac("");
        }
    }

    return (
        <div className="p-4 md:p-6">
            <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">
                        Dispositivi LoRaWAN
                    </h1>
                    <p className="text-sm text-slate-500">
                        Gestione e assegnazione dei sensori LoRaWAN ai frighi
                    </p>
                </div>

                <button
                    onClick={loadAll}
                    disabled={loading}
                    className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
                >
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                    Aggiorna
                </button>
            </div>

            <div className="mb-5 grid gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 md:grid-cols-[1fr_auto]">
                <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Cerca per DevEUI, MAC, modello o frigo..."
                        className="w-full rounded-xl border border-slate-300 py-2 pl-10 pr-3 text-sm outline-none focus:border-slate-500"
                    />
                </div>

                <label className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700">
                    <input
                        type="checkbox"
                        checked={onlyUnassigned}
                        onChange={(e) => setOnlyUnassigned(e.target.checked)}
                    />
                    Solo non assegnati
                </label>
            </div>

            <div className="mb-4">
                <button
                    onClick={loadAll}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                    Applica filtri
                </button>
            </div>

            {error ? (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            ) : null}

            {success ? (
                <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                    {success}
                </div>
            ) : null}

            {loading ? (
                <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200">
                    Caricamento dispositivi...
                </div>
            ) : filteredDevices.length === 0 ? (
                <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200">
                    Nessun dispositivo LoRaWAN trovato.
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredDevices.map((item) => {
                        const selectedFrigo = selectedFrigoByMac[item.mac_address] || "";
                        const manualFrigo = manualFrigoByMac[item.mac_address] || selectedFrigo;
                        const frigoSearch = frigoSearchByMac[item.mac_address] || "";
                        const frighiSuggeriti = getFrighiForDevice(item);
                        const isSaving = savingMac === item.mac_address;

                        return (
                            <div
                                key={item.mac_address}
                                className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200"
                            >
                                <div className="mb-4 grid gap-4 md:grid-cols-[1.3fr_1fr]">
                                    <div>
                                        <div className="mb-2 flex flex-wrap items-center gap-2">
                                            <span className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
                                                <Wifi className="h-3.5 w-3.5" />
                                                LoRaWAN
                                            </span>

                                            {item.assegnato ? (
                                                <span className="inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
                                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                                    Assegnato
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                                                    <AlertTriangle className="h-3.5 w-3.5" />
                                                    Non assegnato
                                                </span>
                                            )}
                                        </div>

                                        <h2 className="text-lg font-semibold text-slate-800">
                                            {item.device_model || "Dispositivo LoRaWAN"}
                                        </h2>

                                        <div className="mt-2 space-y-1 text-sm text-slate-600">
                                            <div>
                                                <span className="font-medium">MAC / DevEUI:</span>{" "}
                                                {item.mac_address}
                                            </div>
                                            <div>
                                                <span className="font-medium">External Device ID:</span>{" "}
                                                {item.external_device_id || "â€”"}
                                            </div>
                                            <div>
                                                <span className="font-medium">Ultimo aggiornamento:</span>{" "}
                                                {fmtDateTime(item.last_seen)}
                                            </div>
                                            <div>
                                                <span className="font-medium">Frigo attuale:</span>{" "}
                                                {item.frigo_nome || "Nessuno"}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="rounded-xl border border-slate-200 p-3">
                                            <div className="mb-1 flex items-center gap-2 text-xs font-medium text-slate-500">
                                                <Thermometer className="h-4 w-4" />
                                                Temperatura
                                            </div>
                                            <div className={`text-lg font-bold ${statusColor(item)}`}>
                                                {fmtTemp(item.temp_ultima)}
                                            </div>
                                        </div>

                                        <div className="rounded-xl border border-slate-200 p-3">
                                            <div className="mb-1 flex items-center gap-2 text-xs font-medium text-slate-500">
                                                <Battery className="h-4 w-4" />
                                                Batteria
                                            </div>
                                            <div className="text-lg font-bold text-slate-800">
                                                {fmtBattery(item.battery_level)}
                                            </div>
                                        </div>

                                        <div className="rounded-xl border border-slate-200 p-3">
                                            <div className="mb-1 flex items-center gap-2 text-xs font-medium text-slate-500">
                                                <AlertTriangle className="h-4 w-4" />
                                                Alert
                                            </div>
                                            <div
                                                className={`text-lg font-bold ${item.alert_ultimo ? "text-red-600" : "text-green-600"
                                                    }`}
                                            >
                                                {item.alert_ultimo ? "SI" : "NO"}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid gap-3 border-t border-slate-100 pt-4 xl:grid-cols-[1fr_0.8fr_auto]">
                                    <div className="space-y-2">
                                        <label className="mb-1 block text-sm font-medium text-slate-700">
                                            Cerca e scegli frigo
                                        </label>
                                        <div className="relative">
                                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                            <input
                                                type="text"
                                                value={frigoSearch}
                                                onChange={(e) =>
                                                    setFrigoSearchByMac((prev) => ({
                                                        ...prev,
                                                        [item.mac_address]: e.target.value,
                                                    }))
                                                }
                                                placeholder="Filtra per nome, ID, azienda o MAC frigo..."
                                                className="w-full rounded-xl border border-slate-300 py-2 pl-10 pr-3 text-sm outline-none focus:border-slate-500"
                                            />
                                        </div>
                                        <select
                                            value={selectedFrigo}
                                            onChange={(e) => {
                                                const next = e.target.value;
                                                setSelectedFrigoByMac((prev) => ({
                                                    ...prev,
                                                    [item.mac_address]: next,
                                                }));
                                                setManualFrigoByMac((prev) => ({
                                                    ...prev,
                                                    [item.mac_address]: next,
                                                }));
                                            }}
                                            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                                        >
                                            <option value="">Seleziona un frigo...</option>
                                            {frighiSuggeriti.map((f) => (
                                                <option key={f.id} value={String(f.id)}>
                                                    {frigoLabel(f)}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="text-xs text-slate-500">
                                            Mostro fino a 40 risultati. Usa il filtro o inserisci direttamente l'ID.
                                        </div>
                                    </div>

                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-slate-700">
                                            Frigo ID diretto
                                        </label>
                                        <input
                                            type="number"
                                            inputMode="numeric"
                                            min="1"
                                            value={manualFrigo}
                                            onChange={(e) =>
                                                setManualFrigoByMac((prev) => ({
                                                    ...prev,
                                                    [item.mac_address]: e.target.value,
                                                }))
                                            }
                                            placeholder="es. 132"
                                            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                                        />
                                        {manualFrigo ? (
                                            <div className="mt-2 text-xs text-slate-500">
                                                Assegno al frigo #{manualFrigo}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="flex items-end">
                                        <button
                                            onClick={() => handleAssign(item, false)}
                                            disabled={isSaving || !manualFrigo}
                                            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                                        >
                                            <Link2 className="h-4 w-4" />
                                            {isSaving ? "Assegnazione..." : "Assegna"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
